import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

// SearchableSelect is a typeahead combobox: an <input> that filters a
// dropdown of options as the user types, with keyboard navigation. Built
// on native elements (no headless-ui / cmdk dependency yet) — when we
// stand up the design-system layer in plan milestone 0, this gets
// replaced with the shadcn/Radix Combobox primitive.
//
// Accessibility: ARIA combobox 1.2 pattern (role=combobox + role=listbox).
// Keyboard: ArrowDown/ArrowUp navigate, Enter selects, Esc closes.

export interface SearchableOption {
  value: string;
  label: string;
  // Optional secondary line shown to the right (e.g. native language name).
  hint?: string;
}

export interface SearchableSelectProps {
  id?: string;
  labelKey: string; // i18n key for <label>
  placeholderKey: string; // i18n key for input placeholder
  emptyKey?: string; // i18n key for "no results"
  value: string;
  options: readonly SearchableOption[];
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
}

export function SearchableSelect({
  id,
  labelKey,
  placeholderKey,
  emptyKey = 'common.noResults',
  value,
  options,
  onChange,
  required,
  disabled,
}: SearchableSelectProps) {
  const { t } = useTranslation('common');
  const reactId = useId();
  const inputId = id ?? `select-${reactId}`;
  const listId = `${inputId}-listbox`;

  // Display value mirrors the selected option's label until the user types
  // to filter; once they type, we treat the input as a query.
  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const [query, setQuery] = useState<string>(selected?.label ?? '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the visible text in sync when `value` changes externally
  // (e.g. browser-locale auto-fill).
  useEffect(() => {
    setQuery(selected?.label ?? '');
  }, [selected]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        // If user closed without picking, snap back to the selected label.
        setQuery(selected?.label ?? '');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, selected]);

  const filtered = useMemo(() => {
    if (!query || query === selected?.label) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, selected]);

  // Clamp highlight when the filtered set shrinks.
  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(Math.max(0, filtered.length - 1));
  }, [filtered, highlight]);

  const select = (opt: SearchableOption) => {
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      const opt = filtered[highlight];
      if (open && opt) {
        e.preventDefault();
        select(opt);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery(selected?.label ?? '');
    }
  };

  return (
    <div className="form__field" ref={containerRef}>
      <label htmlFor={inputId}>{t(labelKey)}</label>
      <div className="select">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          required={required}
          disabled={disabled}
          placeholder={t(placeholderKey)}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {open && (
          <ul
            id={listId}
            role="listbox"
            className="select__list"
          >
            {filtered.length === 0 && (
              <li className="select__empty" role="presentation">
                {t(emptyKey)}
              </li>
            )}
            {filtered.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                className={
                  'select__option' +
                  (i === highlight ? ' select__option--active' : '') +
                  (opt.value === value ? ' select__option--selected' : '')
                }
                // Use onMouseDown so the click fires before the input's
                // blur (which would close the list first).
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(opt);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span>{opt.label}</span>
                {opt.hint && <span className="select__hint">{opt.hint}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
